// Group chats (artemisapi docs/client-guide-group-chats.md): partners chat in one conversation and
// bring Artemis in with @Artemis. Members: [{ UserId, Name, Initials, AvatarUrl, Role }].

export const isGroup = row => !!row && row.kind === 'group'

const firstName = name => String(name || '').trim().split(/\s+/)[0] || ''

// Who can be @mentioned: Artemis, then the other members. token is what goes in the text ("@Jake");
// two members with one first name add their last initial ("@JakeM").
export const mentionOptions = (members = [], meId = null) => {
  const others = members.filter(m => m && m.UserId != null && String(m.UserId) !== String(meId))
  const counts = {}
  others.forEach(m => { const f = firstName(m.Name).toLowerCase(); counts[f] = (counts[f] || 0) + 1 })
  return [
    { key: 'artemis', token: 'Artemis', label: 'Artemis', hint: 'Agent', ai: true },
    ...others.map(m => {
      const parts = String(m.Name || '').trim().split(/\s+/).filter(Boolean)
      const first = parts[0] || `User${m.UserId}`
      const token = counts[first.toLowerCase()] > 1 && parts[1] ? `${first}${parts[1][0]}` : first
      return { key: m.UserId, token, label: m.Name || m.Initials || 'Member', initials: m.Initials || '?', avatarUrl: m.AvatarUrl || null, hint: 'Notify' }
    })
  ]
}

// The Mentions to send: each @token in the text that names an option, once, in order.
export const mentionsFromText = (text, options = []) => {
  const byToken = new Map(options.map(o => [o.token.toLowerCase(), o.key]))
  const out = []
  ;(String(text || '').match(/@[\w]+/g) || []).forEach(t => {
    const key = byToken.get(t.slice(1).toLowerCase())
    if (key != null && !out.some(k => String(k) === String(key))) out.push(key)
  })
  return out
}

// The text split for display: [{ text, mention: bool, me: bool }]. A mention is an @token for a member
// or Artemis; me marks one naming the viewer, who is shown it as "@You".
export const mentionSegments = (text, members = [], meId = null) => {
  const tokens = new Map(mentionOptions(members, null).map(o => [o.token.toLowerCase(), o.key]))
  return String(text || '').split(/(@[\w]+)/).filter(Boolean).map(part => {
    if (part[0] !== '@') return { text: part, mention: false, me: false }
    const key = tokens.get(part.slice(1).toLowerCase())
    if (key == null) return { text: part, mention: false, me: false }
    const me = meId != null && String(key) === String(meId)
    return { text: me ? '@You' : part, mention: true, me }
  })
}

// Who else is typing, from the poll's Typing [{ UserId, Name }]: "Alex is typing…", "Alex and Sam are
// typing…", "Several people are typing…"; null when no one is.
export const typingLine = (typing = []) => {
  const names = (Array.isArray(typing) ? typing : []).map(t => firstName(t && t.Name) || 'Someone')
  if (names.length === 0) return null
  if (names.length === 1) return `${names[0]} is typing…`
  if (names.length === 2) return `${names[0]} and ${names[1]} are typing…`
  return 'Several people are typing…'
}

// How often to tell the server this user is still typing (artemisapi shows it for 6 s per POST).
export const TYPING_EVERY_MS = 3000

// The header's second line.
export const membersLine = (members = [], meId = null) => {
  const others = members.filter(m => String(m.UserId) !== String(meId))
  if (others.length === 0) return 'Just you and Artemis'
  return `${others.map(m => firstName(m.Name) || m.Initials || 'Member').join(', ')} · Artemis joins when @mentioned`
}

// Polled turns merged with the chat on screen: the server's turns in order, except that a live turn
// of this user's (not loaded from the server, so its id isn't v3-…) is kept over the server's copy
// of the same question, since it has the reply's live state; live turns not saved yet go last.
export const mergePolled = (local = [], server = []) => {
  const live = new Map(local.filter(t => !String(t.id).startsWith('v3-') && t.questionMessageId).map(t => [t.questionMessageId, t]))
  const merged = server.map(t => live.get(t.questionMessageId) || t)
  const known = new Set(server.map(t => t.questionMessageId))
  const pending = local.filter(t => !String(t.id).startsWith('v3-') && (!t.questionMessageId || !known.has(t.questionMessageId)))
  return [...merged, ...pending]
}

// The highest Seq loaded, and where to poll from: just before the oldest reply still being written,
// so it's fetched again until it finishes.
export const pollFrom = (raw = []) => {
  const seqs = raw.map(m => m.Seq).filter(Number.isFinite)
  const last = seqs.length ? Math.max(...seqs) : 0
  const streaming = raw.filter(m => m.Status === 'streaming').map(m => m.Seq).filter(Number.isFinite)
  return streaming.length ? Math.min(...streaming) - 1 : last
}

// The "@you" toast (as on Android): unread mentions from GET Notifications not toasted yet. The first look this
// page takes only remembers what's there, so opening BaseMap doesn't replay old mentions; one in the chat the user
// has open isn't toasted either. seen is a Set of MessageIds, updated here. → the mentions to toast, newest first.
export const mentionsToToast = (items = [], seen, { first = false, openConversationId = null } = {}) => {
  const fresh = items.filter(n => n && n.MessageId && !seen.has(n.MessageId))
  fresh.forEach(n => seen.add(n.MessageId))
  if (first) return []
  return fresh.filter(n => String(n.ConversationId) !== String(openConversationId))
}

// "Jake mentioned you · Selway Elk"
export const mentionToastText = n => `${(n.From && n.From.trim().split(/\s+/)[0]) || 'A partner'} mentioned you${n.Title ? ` · ${n.Title}` : ''}`
